import React, { useState, useRef, useEffect } from "react";
import { Form } from "react-router-dom";

// Redux Tools below
import { useDispatch, useSelector } from "react-redux";
import { Box, Stack } from "@mui/material";
import classes from "./AuthForm.module.scss";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import VisibilitySharpIcon from "@mui/icons-material/VisibilitySharp";
import InputField from "../../../components/ui/Input/InputField";
import Button from "../../../components/ui/button/Button";
import { useNavigate } from "react-router-dom";
import authenticateUser from "../../../components/auth/AuthenticateUser";
import VerificationModal, {
    VerificationModalRef,
} from "../../../components/ui/verification-modal/VerificationModal";
import { resendVerificationCode } from "../../../utils/verificationUtils";
import { RootState, AppDispatch } from "../../../store/store";
import { selectFullProfile, selectProfileLoading } from "../../../store/slices/profileSlice";
import { selectIsAuthenticated } from "../../../store/slices/authSlice";

const STRINGS = {
    WELCOME_BACK: "Welcome back!",
    SIGN_IN_CONTINUE: "Sign in to take advantage of new opportunities",
    EMAIL_LABEL: "Email",
    EMAIL_PLACEHOLDER: "Enter email address",
    PASSWORD_LABEL: "Password",
    PASSWORD_PLACEHOLDER: "Enter password",
    FORGOT_PASSWORD: "Forgot password?",
    LOGIN_BUTTON: "Login",
    SIGN_IN_WITH: "Sign in with",
    DONT_HAVE_ACCOUNT: "Don't have an account?",
    SIGN_UP_NOW: "Sign Up",
    REQUIRED_FIELDS_ERROR: "This field is required",
    LOGIN_FAILED: "Login failed. Please check your credentials and try again.",
    VERIFICATION_ERROR: "Couldn't send verification code. Please try again.",
    VERIFICATION_SUCCESS_ERROR: "Login failed after verification. 😟 Please try again.",
} as const;

interface LoginProps {
    onClickSignup: (form: string) => void;
}

const Login: React.FC<LoginProps> = ({ onClickSignup }: LoginProps) => {
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const [showPassword, setShowPassword] = useState(false);
    const [errors, setErrors] = useState({
        email: false,
        password: false,
    });
    const [errorMessage, setErrorMessage] = useState(""); // Stores error message

    // Reference to the verification modal for unverified accounts
    const verificationModalRef = useRef<VerificationModalRef>(null);

    // *** NEW: Selectors for redirection logic ***
    const isAuthenticated = useSelector(selectIsAuthenticated);
    const fullProfile = useSelector(selectFullProfile);
    const isProfileLoading = useSelector(selectProfileLoading);

    const handledOnClickSignup = () => {
        onClickSignup("signup");
    };

    const handledOnClickForgotPassword = () => {
        onClickSignup("forgotpassword");
    };

    const [formData, setFormData] = useState({
        email: "",
        password: "",
    });

    const handelShowPassword = () => {
        setShowPassword((prev) => !prev);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { id, value } = e.target;
        setFormData({
            ...formData,
            [id]: value,
        });
        // Clear error for the fields when user types
        setErrors({ email: false, password: false });
        setErrorMessage("");
    };

    // *** NEW: useEffect for handling navigation after login and profile fetch ***
    useEffect(() => {
        // After login, always navigate to the newsfeed.
        // We assume users who are logging in have already completed the initial setup.
        if (isAuthenticated) {
            navigate("/newsfeed");
        }
        // Dependencies: run when auth state changes or profile loading state finishes
    }, [isAuthenticated, navigate]);

    // Handle the success of verification (user has now confirmed their account)
    const handleVerificationSuccess = async () => {
        try {
            // After verification is successful, attempt login again
            await authenticateUser(formData.email, formData.password, dispatch, navigate);
        } catch (error) {
            setErrors({ email: true, password: true });
            setErrorMessage(STRINGS.VERIFICATION_SUCCESS_ERROR);
        }
    };

    const onSubmit = async (e: React.MouseEvent<HTMLButtonElement>) => {
        // Prevent default form submission
        e.preventDefault();

        // Check each field individually
        const newErrors = {
            email: !formData.email,
            password: !formData.password,
        };
        setErrors(newErrors);

        // If any field is empty, don't proceed
        if (newErrors.email || newErrors.password) {
            setErrorMessage(STRINGS.REQUIRED_FIELDS_ERROR);
            return;
        }

        try {
            // Call the authenticateUser function to log in the user and redirect to the newsfeed page if successful.
            await authenticateUser(formData.email, formData.password, dispatch, navigate);
        } catch (error: any) {
            // Check if this is an unverified user case
            if (error.name === "UserNotConfirmedException") {
                // User exists but isn't verified - send a new verification code
                try {
                    // Resend the verification code to the user's email address
                    await resendVerificationCode(formData.email);

                    // Show verification modal
                    verificationModalRef.current?.open();

                    // Clear the error message as we're handling this case specially
                    setErrors({ email: false, password: false });
                    setErrorMessage("");
                } catch (resendError) {
                    // If the verification code couldn't be sent, set an error message
                    setErrors({ email: true, password: false });
                    setErrorMessage(STRINGS.VERIFICATION_ERROR);
                }
            } else {
                // Handle other login errors
                setErrors({ email: true, password: false });
                setErrorMessage(STRINGS.LOGIN_FAILED);
            }
        }
    };

    return (
        <Stack spacing={2} className={classes.authFormContainer}>
            {/* Verification Modal for Unverified Users trying to login */}
            <VerificationModal
                ref={verificationModalRef}
                email={formData.email}
                onVerificationSuccess={handleVerificationSuccess}
                onCancel={() => verificationModalRef.current?.close()}
            />

            {/* Form */}
            <Box>
                <Form className={classes.authForm}>
                    {/* Welcome Text */}
                    <div className={classes.welcomeText}>
                        <h4>{STRINGS.WELCOME_BACK}</h4>
                        <p>{STRINGS.SIGN_IN_CONTINUE}</p>
                    </div>

                    {/* Form starts from here */}
                    <InputField
                        name="email"
                        label={STRINGS.EMAIL_LABEL}
                        type="email"
                        placeholder={STRINGS.EMAIL_PLACEHOLDER}
                        value={formData.email}
                        error={errors.email}
                        helperText={errors.email ? errorMessage : ""}
                        onChange={handleInputChange}
                    />

                    <InputField
                        name="password"
                        label={STRINGS.PASSWORD_LABEL}
                        type={showPassword ? "text" : "password"}
                        placeholder={STRINGS.PASSWORD_PLACEHOLDER}
                        value={formData.password}
                        error={errors.password}
                        helperText={errors.password ? errorMessage : ""}
                        onChange={handleInputChange}
                        icon={
                            showPassword ? (
                                <VisibilitySharpIcon onClick={handelShowPassword} />
                            ) : (
                                <VisibilityOffIcon onClick={handelShowPassword} />
                            )
                        }
                        rightLabelLink={STRINGS.FORGOT_PASSWORD}
                        onClickRightlabel={handledOnClickForgotPassword}
                    />

                    <Stack spacing={2}>
                        <div>
                            <Button
                                label={STRINGS.LOGIN_BUTTON}
                                size="small"
                                type="submit"
                                variant="primary"
                                onClick={onSubmit}
                            />
                        </div>

                        {/* TODO: Uncomment the following "Sign Up With LinkedIn" once the Logging in with LinkedIn functionality works (again) */}
                        {/* <p className={classes.alternativeAuthWith}>{STRINGS.SIGN_IN_WITH}</p>

                          <img
                              src={linkedinLogo}
                              alt="linkedin"
                              className={classes.linkedinLogo}
                          /> */}
                        <div className={classes.alternativeAuthLink}>
                            <p>
                                {STRINGS.DONT_HAVE_ACCOUNT}{" "}
                                <span
                                    onClick={handledOnClickSignup}
                                    className={classes.underlineText}
                                >
                                    {STRINGS.SIGN_UP_NOW}
                                </span>
                            </p>
                        </div>
                    </Stack>
                </Form>
            </Box>
        </Stack>
    );
};

export default Login;
